import type { MemberQuiet, Notify } from '../../api/types'
import type { LocalChannel } from '../../db/db'
import { isRestricted } from '../../lib/posting'
import { FOREVER } from './muteOptions'

/**
 * Whether a conversation will notify someone, and why not.
 *
 * Two things silence it: a mute, which runs out, and "Nothing", which is a standing choice. They read differently
 * everywhere, and where both are set the standing choice is what is said: it is the one that outlives the other.
 */
export type QuietStyle = 'short' | 'long' | 'partner'

export function quietState(channel: Pick<LocalChannel, 'notify' | 'mutedUntil'>, now = Date.now()): MemberQuiet {
  if (channel.notify === 'none') return { kind: 'off', until: null }
  if (!channel.mutedUntil) return null
  if (new Date(channel.mutedUntil).getTime() <= now) return null
  return { kind: 'muted', until: channel.mutedUntil === FOREVER ? null : channel.mutedUntil }
}

/** When a mute ends, as someone would say it: the time today, "tomorrow 09:15", or the day. Null where it has no end. */
export function muteEnds(until: string | null, now = Date.now()): string | null {
  if (!until || until === FOREVER) return null
  const end = new Date(until)
  const time = end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  const day = (ms: number) => new Date(ms).toLocaleDateString('en-GB')
  if (day(end.getTime()) === day(now)) return time
  if (day(end.getTime()) === day(now + 24 * 60 * 60 * 1000)) return `tomorrow ${time}`
  return `${end.toLocaleDateString('en-GB', { weekday: 'short' })} ${time}`
}

/**
 * What a state says, for where it is being said: `short` on the conversation itself, `long` on the settings screen,
 * `partner` to whoever is writing to the person who is quiet (they are told what it means for their message).
 */
export function quietLabel(quiet: MemberQuiet, style: QuietStyle, now = Date.now(), name = ''): string | null {
  if (!quiet) return null
  const ends = muteEnds(quiet.until, now)
  if (style === 'partner') {
    if (quiet.kind === 'off') return `${name} has notifications off for this chat. They won’t be notified about your messages.`
    return ends ? `${name} has muted this chat until ${ends}. They won’t be notified before then.` : `${name} has muted this chat. They won’t be notified about your messages.`
  }
  if (style === 'long') {
    if (quiet.kind === 'off') return 'New messages here never notify you. They still count as unread.'
    return ends ? `Nothing here will notify you until ${ends}, unless someone @mentions you.` : 'Nothing here will notify you until you unmute it, unless someone @mentions you.'
  }
  if (quiet.kind === 'off') return 'Notifications off'
  return ends ? `Muted until ${ends}` : 'Muted'
}

/** What a conversation notifies about when nobody has chosen: a channel anyone can post in is busy, so only mentions. */
export function defaultNotify(channel: Pick<LocalChannel, 'type' | 'posting'>): Notify {
  return channel.type === 'channel' && !isRestricted(channel) ? 'mentions' : 'all'
}
